import Service, { service } from '@ember/service';

/**
 * Message Processor Service
 *
 * Handles processing of WebSocket/MQTT messages for event data updates.
 * Processes reference snapshots, reference updates, and timing events.
 */
export default class MessageProcessorService extends Service {
  @service eventData;
  @service sequenceTracker;
  @service dataMerger;

  /**
   * Process a single MQTT message
   * If merge is in progress for this event, queue the message for later processing
   */
  processMessage(message) {
    // Get eventId from message
    let eventId = message.eventUuid || message.eventId;
    if (!eventId) {
      const dataEventUuid = message.data?.eventUuid;
      if (dataEventUuid) {
        eventId = dataEventUuid;
      }
    }

    // Check if merge is locked - if so, queue the message
    if (eventId && this.dataMerger.isMergeLocked(eventId)) {
      this.dataMerger.queueMessage(eventId, message);
      return;
    }

    // Process immediately
    this._processMessageInternal(message);
  }

  /**
   * Internal message processing (called directly or after merge lock release)
   */
  _processMessageInternal(message) {
    // Support both 'messageType' and 'type' fields (Lambda uses 'type' for timing updates)
    const msgType = message.messageType || message.type;
    if (!message || !msgType) {
      console.warn('[MessageProcessor] Invalid message (no messageType or type):', message);
      return;
    }

    // Messages now use eventUuid (not numeric eventId)
    // We store data under the UUID
    let eventId = message.eventUuid || message.eventId;
    if (!this.eventData.events.has(eventId)) {
      // Try to get UUID from nested data
      const dataEventUuid = message.data?.eventUuid;
      if (dataEventUuid && this.eventData.events.has(dataEventUuid)) {
        eventId = dataEventUuid;
      } else {
        console.warn('[MessageProcessor] Message for unsubscribed event:', eventId, 'from message:', message.eventUuid || message.eventId);
        return;
      }
    }

    console.log(`[MessageProcessor] Processing ${msgType} for event ${eventId}`);

    // Check for sequence gaps on timing messages (triggers refresh if gap detected)
    if (msgType === 'timing_event' || msgType === 'timing_update' ||
        msgType === 'timing_events_batch' || msgType === 'timing_batch_update') {
      // Get sequence from message (single event) or last event in batch
      const sequence = message.sequence ||
                       message.lastSequence ||
                       (message.events && message.events[message.events.length - 1]?.sequence);
      this.sequenceTracker.checkSequenceGap(eventId, sequence);
    }

    switch (msgType) {
      case 'reference_snapshot':
        this.handleReferenceSnapshot(eventId, message);
        break;
      case 'reference_update':
        this.handleReferenceUpdate(eventId, message);
        break;
      case 'timing_event':
      case 'timing_update': // Lambda broadcasts use 'timing_update'
        this.handleTimingEvent(eventId, message);
        break;
      case 'timing_events_batch':
      case 'timing_batch_update': // Lambda broadcasts use 'timing_batch_update'
        this.handleTimingEventsBatch(eventId, message);
        break;
      case 'snapshot_batch':
        this.handleTimingSnapshotBatch(eventId, message);
        break;
      case 'snapshot_clear':
        this.handleTimingSnapshotClear(eventId, message);
        break;
      default:
        console.log(`[MessageProcessor] Unknown message type: ${msgType}`);
    }

    // Update last updated timestamp and increment version to trigger reactivity
    const eventData = this.eventData.events.get(eventId);
    eventData.lastUpdated = new Date();
    this.eventData._dataVersion++;
  }

  /**
   * Handle reference data snapshot message
   * All data is keyed by uuid (for reference data) or entryUuid (for timing-related records)
   */
  handleReferenceSnapshot(eventId, message) {
    const eventData = this.eventData.events.get(eventId);
    const { dataType, data } = message;

    console.log(`[MessageProcessor] Reference snapshot: ${dataType}, count: ${Array.isArray(data) ? data.length : 1}`);

    switch (dataType) {
      case 'event':
        eventData.event = data;
        eventData.snapshotProgress.event = true;
        break;

      case 'classgroups':
        data.forEach(cg => eventData.classGroups.set(cg.uuid, cg));
        eventData.snapshotProgress.classgroups = true;
        break;

      case 'classes':
        data.forEach(cls => eventData.classes.set(cls.uuid, cls));
        eventData.snapshotProgress.classes = true;
        break;

      case 'entries':
        // Entries come in batches, keyed by uuid
        data.forEach(entry => eventData.entries.set(entry.uuid, entry));

        // Mark complete only when we receive the last batch
        if (message.batchIndex === message.totalBatches) {
          eventData.snapshotProgress.entries = true;
        }
        break;

      case 'courses':
        data.forEach(course => eventData.courses.set(course.uuid, course));
        eventData.snapshotProgress.courses = true;
        break;

      case 'tietimingrecords':
        // Keyed by entryUuid
        data.forEach(tie => eventData.tieTimingRecords.set(tie.entryUuid, tie));
        eventData.snapshotProgress.tietimingrecords = true;
        break;

      case 'penaltytimingrecords':
        // Group penalties by entryUuid
        data.forEach(penalty => {
          const entryUuid = penalty.entryUuid;
          const existing = eventData.penaltyTimingRecords.get(entryUuid) || [];
          // Create new array for reactivity
          eventData.penaltyTimingRecords.set(entryUuid, [...existing, penalty]);
        });
        eventData.snapshotProgress.penaltytimingrecords = true;
        break;

      default:
        console.warn(`[MessageProcessor] Unknown snapshot dataType: ${dataType}`);
    }

    // Check if all snapshots are complete
    this.checkSnapshotCompletion(eventId);
  }

  /**
   * Handle reference data update message
   * Message format: { messageType: 'reference_update', changes: { entries: {...}, classGroups: {...}, ... } }
   * Each data type in changes has: { added: [], updated: [], deleted: [] }
   * All items use uuid as key
   */
  handleReferenceUpdate(eventId, message) {
    const eventData = this.eventData.events.get(eventId);
    const { changes } = message;

    console.log(`[MessageProcessor] Reference update received:`, changes);

    // Process each data type in the changes object
    for (const [dataType, change] of Object.entries(changes)) {
      if (!change) continue;

      console.log(`[MessageProcessor] Processing ${dataType}:`, {
        added: change.added?.length || 0,
        updated: change.updated?.length || 0,
        deleted: change.deleted?.length || 0
      });

      switch (dataType) {
        case 'event':
          if (change.updated) {
            eventData.event = change.updated;
          }
          break;

        case 'classGroups':
          this.applyArrayChangesByUuid(eventData.classGroups, change);
          break;

        case 'classes':
          this.applyArrayChangesByUuid(eventData.classes, change);
          break;

        case 'entries':
          this.applyArrayChangesByUuid(eventData.entries, change);
          break;

        case 'courses':
          this.applyArrayChangesByUuid(eventData.courses, change);
          break;

        case 'tieTimingRecords':
          // Keyed by entryUuid
          if (change.added) change.added.forEach(item => eventData.tieTimingRecords.set(item.entryUuid, item));
          if (change.updated) change.updated.forEach(item => eventData.tieTimingRecords.set(item.entryUuid, item));
          if (change.deleted) change.deleted.forEach(uuid => {
            // deleted contains uuid, need to find by iterating or just try to delete
            for (const [key, record] of eventData.tieTimingRecords.entries()) {
              if (record.uuid === uuid) {
                eventData.tieTimingRecords.delete(key);
                break;
              }
            }
          });
          break;

        case 'penaltyTimingRecords':
          // For added penalties, append to the entry's penalty array (keyed by entryUuid)
          if (change.added) {
            change.added.forEach(penalty => {
              const entryUuid = penalty.entryUuid;
              const existing = eventData.penaltyTimingRecords.get(entryUuid) || [];
              // Create new array for reactivity
              eventData.penaltyTimingRecords.set(entryUuid, [...existing, penalty]);
            });
          }
          // For updated penalties, find and replace by uuid
          if (change.updated) {
            change.updated.forEach(penalty => {
              const entryUuid = penalty.entryUuid;
              const penalties = eventData.penaltyTimingRecords.get(entryUuid) || [];
              const idx = penalties.findIndex(p => p.uuid === penalty.uuid);
              let newPenalties;
              if (idx >= 0) {
                // Create new array for reactivity
                newPenalties = [...penalties];
                newPenalties[idx] = penalty;
              } else {
                newPenalties = [...penalties, penalty];
              }
              eventData.penaltyTimingRecords.set(entryUuid, newPenalties);
            });
          }
          // For deleted penalties, remove by uuid
          if (change.deleted) {
            change.deleted.forEach(penaltyUuid => {
              // Need to find which entryUuid this penalty belongs to
              for (const [entryUuid, penalties] of eventData.penaltyTimingRecords.entries()) {
                const filtered = penalties.filter(p => p.uuid !== penaltyUuid);
                if (filtered.length !== penalties.length) {
                  eventData.penaltyTimingRecords.set(entryUuid, filtered);
                  break;
                }
              }
            });
          }
          break;

        default:
          console.warn(`[MessageProcessor] Unknown reference update dataType: ${dataType}`);
      }
    }
  }

  /**
   * Apply array changes (added, updated, deleted) to a Map using uuid as key
   */
  applyArrayChangesByUuid(map, change) {
    if (change.added) {
      change.added.forEach(item => map.set(item.uuid, item));
    }
    if (change.updated) {
      change.updated.forEach(item => map.set(item.uuid, item));
    }
    if (change.deleted) {
      // deleted contains uuids
      change.deleted.forEach(uuid => map.delete(uuid));
    }
  }

  /**
   * Handle single timing event
   * Timing records are keyed by entryUuid (not numeric EntryId)
   */
  handleTimingEvent(eventId, message) {
    const eventData = this.eventData.events.get(eventId);
    const { recordType, operation, data } = message;

    // Use entryUuid (UUID-based format)
    const entryUuid = data?.entryUuid || null;
    const recordUuid = data?.uuid;

    console.log(`[MessageProcessor] Timing event: ${operation} ${recordType}`, {
      entryUuid,
      recordUuid,
      hasFinishTime: !!data?.finishTime,
      finishTime: data?.finishTime,
      hasStartTime: !!data?.startTime,
      startTime: data?.startTime,
      data: data
    });

    // Note: entryUuid can be null for unassociated records - don't return early

    switch (recordType) {
      case 'start': {
        const removeExistingByUUID = () => {
          if (!recordUuid) return null;
          for (const [key, record] of eventData.startRecords.entries()) {
            if (record?.uuid === recordUuid) {
              eventData.startRecords.delete(key);
              console.log(`[MessageProcessor] Removed start record from entry ${key} (uuid: ${recordUuid})`);
              return key;
            }
          }
          return null;
        };

        if (operation === 'DELETE') {
          removeExistingByUUID();
        } else if (operation === 'INSERT') {
          if (entryUuid) {
            eventData.startRecords.set(entryUuid, data);
            console.log(`[MessageProcessor] Inserted start record for entry ${entryUuid}`);
          }
        } else {
          // UPDATE
          removeExistingByUUID();
          if (entryUuid) {
            eventData.startRecords.set(entryUuid, data);
            console.log(`[MessageProcessor] Updated start record for entry ${entryUuid}`);
          }
        }
        break;
      }

      case 'finish': {
        const removeExistingByUUID = () => {
          if (!recordUuid) return null;
          for (const [key, record] of eventData.finishRecords.entries()) {
            if (record?.uuid === recordUuid) {
              eventData.finishRecords.delete(key);
              console.log(`[MessageProcessor] Removed finish record from entry ${key} (uuid: ${recordUuid})`);
              return key;
            }
          }
          return null;
        };

        // If two records exist for the same entry (chip-data imports can
        // produce this even though the UI prevents it), the record with the
        // higher local id wins. Same-record updates (same uuid) always apply.
        const shouldReplaceExisting = () => {
          if (!entryUuid) return false;
          const existing = eventData.finishRecords.get(entryUuid);
          if (!existing) return true;
          if (existing.uuid === data.uuid) return true;
          return (data.id ?? 0) >= (existing.id ?? 0);
        };

        if (operation === 'DELETE') {
          removeExistingByUUID();
        } else if (operation === 'INSERT') {
          if (entryUuid && shouldReplaceExisting()) {
            eventData.finishRecords.set(entryUuid, data);
            console.log(`[MessageProcessor] Inserted finish record for entry ${entryUuid} (id: ${data.id})`);
          } else if (entryUuid) {
            console.log(`[MessageProcessor] Skipping INSERT finish for entry ${entryUuid}: existing record id ${eventData.finishRecords.get(entryUuid)?.id} beats ${data.id}`);
          }
        } else {
          // UPDATE: authoritative change from backend - always apply, even when
          // finishTime is null (impulse unlinked, manual correction). Previously
          // we skipped null UPDATEs to dodge reassignment races, but that left
          // CloudLive showing deleted finish times forever.
          removeExistingByUUID();
          if (entryUuid && shouldReplaceExisting()) {
            eventData.finishRecords.set(entryUuid, data);
            console.log(`[MessageProcessor] Updated finish record for entry ${entryUuid} (id: ${data.id}, finishTime: ${data.finishTime})`);
          } else if (entryUuid) {
            console.log(`[MessageProcessor] Skipping UPDATE finish for entry ${entryUuid}: existing record id ${eventData.finishRecords.get(entryUuid)?.id} beats ${data.id}`);
          }
        }
        break;
      }

      case 'intermediate': {
        // Helper to find and remove intermediate record by UUID from ANY entry's array
        const removeExistingByUUID = () => {
          if (!recordUuid) return null;
          for (const [key, records] of eventData.intermediateRecords.entries()) {
            const existingIndex = records.findIndex(r => r.uuid === recordUuid);
            if (existingIndex >= 0) {
              const newRecords = records.filter(r => r.uuid !== recordUuid);
              if (newRecords.length > 0) {
                eventData.intermediateRecords.set(key, newRecords);
              } else {
                eventData.intermediateRecords.delete(key);
              }
              console.log(`[MessageProcessor] Removed intermediate record from entry ${key} (uuid: ${recordUuid})`);
              return key;
            }
          }
          return null;
        };

        if (operation === 'DELETE') {
          removeExistingByUUID();
        } else if (operation === 'INSERT') {
          if (entryUuid) {
            const existingRecords = eventData.intermediateRecords.get(entryUuid) || [];
            // Check for duplicate by UUID before inserting
            const alreadyExists = existingRecords.some(r => r.uuid === recordUuid);
            if (alreadyExists) {
              console.log(`[MessageProcessor] Skipping duplicate intermediate record for entry ${entryUuid} (uuid: ${recordUuid})`);
            } else {
              eventData.intermediateRecords.set(entryUuid, [...existingRecords, data]);
              console.log(`[MessageProcessor] Inserted intermediate record for entry ${entryUuid}`);
            }
          }
        } else {
          // UPDATE: Remove old record from wherever it is, then add at new entryUuid
          removeExistingByUUID();
          if (entryUuid) {
            const existingRecords = eventData.intermediateRecords.get(entryUuid) || [];
            eventData.intermediateRecords.set(entryUuid, [...existingRecords, data]);
            console.log(`[MessageProcessor] Updated intermediate record for entry ${entryUuid}`);
          }
        }
        break;
      }
    }
  }

  /**
   * Handle batch of timing events
   */
  handleTimingEventsBatch(eventId, message) {
    const { events } = message;
    console.log(`[MessageProcessor] Processing timing events batch: ${events.length} events`);

    events.forEach(event => {
      this.handleTimingEvent(eventId, event);
    });
  }

  /**
   * Handle timing snapshot clear
   * Drops all in-memory timing records in lockstep with the backend clearing
   * DynamoDB, so stale data doesn't linger before the snapshot batches arrive.
   */
  handleTimingSnapshotClear(eventId, message) {
    const eventData = this.eventData.events.get(eventId);
    if (!eventData) return;

    console.log(`[MessageProcessor] Snapshot clear for event ${eventId} (snapshotId: ${message.snapshotId})`);

    eventData.startRecords.clear();
    eventData.finishRecords.clear();
    eventData.intermediateRecords.clear();

    eventData.snapshotProgress.timing = false;
    eventData.snapshotComplete = false;
  }

  /**
   * Handle timing snapshot batch
   * Records are keyed by entryUuid
   */
  handleTimingSnapshotBatch(eventId, message) {
    const eventData = this.eventData.events.get(eventId);
    const { recordType, records, isLastBatch } = message;

    console.log(`[MessageProcessor] Timing snapshot batch: ${recordType}, count: ${records.length}, last: ${isLastBatch}`);

    switch (recordType) {
      case 'start':
        records.forEach(record => {
          if (record.entryUuid) {
            eventData.startRecords.set(record.entryUuid, record);
          }
        });
        break;

      case 'finish':
        // Snapshot can contain more than one record per entry when chip-data
        // imports produced duplicates. Keep the higher local id.
        records.forEach(record => {
          if (!record.entryUuid) return;
          const existing = eventData.finishRecords.get(record.entryUuid);
          if (!existing || (record.id ?? 0) >= (existing.id ?? 0)) {
            eventData.finishRecords.set(record.entryUuid, record);
          }
        });
        break;

      case 'intermediate':
        records.forEach(record => {
          if (record.entryUuid) {
            const existing = eventData.intermediateRecords.get(record.entryUuid) || [];
            // Check for duplicate by UUID before inserting
            const alreadyExists = existing.some(r => r.uuid === record.uuid);
            if (!alreadyExists) {
              // Create new array for reactivity
              eventData.intermediateRecords.set(record.entryUuid, [...existing, record]);
            }
          }
        });
        break;
    }

    if (isLastBatch) {
      eventData.snapshotProgress.timing = true;
      this.checkSnapshotCompletion(eventId);
    }
  }

  /**
   * Check if all snapshots are complete
   */
  checkSnapshotCompletion(eventId) {
    const eventData = this.eventData.events.get(eventId);
    const progress = eventData.snapshotProgress;

    const allComplete = progress.event &&
                        progress.classgroups &&
                        progress.classes &&
                        progress.entries &&
                        progress.courses &&
                        progress.tietimingrecords &&
                        progress.penaltytimingrecords &&
                        progress.timing;

    console.log(`[MessageProcessor] checkSnapshotCompletion for event ${eventId}:`, progress, `allComplete: ${allComplete}`);

    if (allComplete && !eventData.snapshotComplete) {
      eventData.snapshotComplete = true;
      console.log(`[MessageProcessor] Snapshot complete for event ${eventId}`);
      // Re-set in TrackedMap to trigger reactivity
      this.eventData.events.set(eventId, eventData);
    }
  }
}
